import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import MainLayout from './layout/MainLayout';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import Dashboard from './pages/Dashboard';
import UsersPage from './pages/UsersPage';
import ServicesPage from './pages/ServicesPage';
import ServiceApplicationsPage from './pages/ServiceApplicationsPage';
import UserRequestsPage from './pages/UserRequestsPage';
import AdRemovalManagementPage from './pages/AdRemovalManagementPage';
import AuditLogsPage from './pages/AuditLogsPage';
import ProfilePage from './pages/ProfilePage';
import AdRemovalPage from './pages/AdRemovalPage';
import ApprovalPendingPage from './pages/ApprovalPendingPage';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import LoadingScreen from './components/LoadingScreen';
import { useAuth } from './context/AuthContext';
import './styles/global.css';

const ServiceApplicationsRoute: React.FC = () => {
  const { user } = useAuth();
  return user?.globalRole === 'ROOT_ADMIN' ? <Navigate to="/user-requests" replace /> : <ServiceApplicationsPage />;
};

const AuditLogsRoute: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) return <LoadingScreen title="권한을 확인하는 중" copy="로그 접근 권한을 검사하고 있습니다." />;

  const canAccess =
    user?.globalRole === 'ROOT_ADMIN' ||
    (user?.services ?? []).some((service) => service.role === 'SERVICE_OWNER');

  return canAccess ? <AuditLogsPage /> : <Navigate to="/" replace />;
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<RegisterPage />} />
          <Route path="/register" element={<Navigate to="/signup" replace />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/approval-management" element={<Navigate to="/user-requests" replace />} />
          <Route
            path="/pending-approval"
            element={
              <ProtectedRoute>
                <ApprovalPendingPage />
              </ProtectedRoute>
            }
          />
          
          <Route path="/" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Dashboard />} />
            <Route path="users" element={
              <AdminRoute>
                <UsersPage />
              </AdminRoute>
            } />
            <Route path="user-requests" element={
              <AdminRoute>
                <UserRequestsPage />
              </AdminRoute>
            } />
            <Route path="services" element={<ServicesPage />} />
            <Route path="service-applications" element={<ServiceApplicationsRoute />} />
            <Route path="ad-removal-management" element={
              <AdminRoute>
                <AdRemovalManagementPage />
              </AdminRoute>
            } />
            <Route path="audit-logs" element={<AuditLogsRoute />} />
            <Route path="ad-removal" element={<AdRemovalPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="introspect" element={<Navigate to="/users" replace />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
